import type { Icon } from '@phosphor-icons/react';
import {
  BookmarkSimpleIcon,
  BriefcaseIcon,
  FileDashedIcon,
  GavelIcon,
  GraphIcon,
  LightbulbIcon,
  ListMagnifyingGlassIcon,
  NotePencilIcon,
  ScalesIcon,
} from '@phosphor-icons/react';
import { DraftMode, ResourceType } from 'gen/api';
import type { TChipColour } from 'ui-library/components/Chip/Chip';
import { WORKFLOWS } from 'utils/roles/types';

export interface IResourceTypeMeta {
  label: string;
  /** Only where adding "s" to the label reads wrong. */
  pluralLabel?: string;
  icon: Icon;
  colour: Exclude<TChipColour, 'clear' | 'white'>;
}

export const UNIFIED_PROJECT_WORKFLOW_RESOURCE_TYPES = [
  [WORKFLOWS.PATENT_DRAFT, ResourceType.PATENT_DRAFT],
  [WORKFLOWS.DISCLOSURE_DRAFT, ResourceType.PATENT_DRAFT],
  [WORKFLOWS.OFFICE_ACTIONS, ResourceType.OFFICE_ACTION_2],
  [WORKFLOWS.GENERAL_ASSISTANT, ResourceType.GENERAL_ASSISTANT],
  [WORKFLOWS.INVENTION_ASSISTANT, ResourceType.INVENTION_ASSISTANT],
  [WORKFLOWS.INVENTION_DISCLOSURE, ResourceType.INVENTION_DISCLOSURE],
  [WORKFLOWS.LITIGATION_ASSISTANT, ResourceType.LITIGATION_ASSISTANT],
  [WORKFLOWS.ANTICIPATION, ResourceType.PUBLICATION_TRACKER_TOPIC],
] as const;

export type UnifiedProjectResourceType = (typeof UNIFIED_PROJECT_WORKFLOW_RESOURCE_TYPES)[number][1];

export const RESOURCE_TYPE_META: Record<UnifiedProjectResourceType, IResourceTypeMeta> = {
  [ResourceType.PATENT_DRAFT]: {
    label: 'Patent Draft',
    icon: NotePencilIcon,
    colour: 'cyan',
  },
  [ResourceType.OFFICE_ACTION_2]: {
    label: 'Office Action',
    icon: GavelIcon,
    colour: 'orange',
  },
  [ResourceType.GENERAL_ASSISTANT]: {
    label: 'IP Project',
    icon: BriefcaseIcon,
    colour: 'blue',
  },
  [ResourceType.INVENTION_ASSISTANT]: {
    label: 'Research Project',
    icon: ListMagnifyingGlassIcon,
    colour: 'purple',
  },
  [ResourceType.INVENTION_DISCLOSURE]: {
    label: 'Novelty Assistant',
    icon: BookmarkSimpleIcon,
    colour: 'yellow',
  },
  [ResourceType.LITIGATION_ASSISTANT]: {
    label: 'Infringement Assistant',
    icon: ScalesIcon,
    colour: 'red',
  },
  [ResourceType.PUBLICATION_TRACKER_TOPIC]: {
    label: 'IP Intelligence',
    pluralLabel: 'IP Intelligence',
    icon: LightbulbIcon,
    colour: 'crimson',
  },
};

export const PATENT_DRAFT_MODE_META: Partial<Record<DraftMode, IResourceTypeMeta>> = {
  [DraftMode.DISCLOSURE]: {
    label: 'Disclosure Draft',
    icon: FileDashedIcon,
    colour: 'green',
  },
  [DraftMode.CHEMICAL_PATENT]: {
    label: 'Patent Draft (Life Sciences & Chemistry)',
    icon: GraphIcon,
    colour: 'cyan',
  },
};
